// Sovereign Crypto & Financial Asset Live Telemetry
// Asynchronously fetches coin prices with persistent client caching

const CACHE_KEY = 'memark_crypto_telemetry_cache';
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

const COIN_MAP = {
  bitcoin: { id: 'bitcoin', symbol: 'BTC' },
  btc: { id: 'bitcoin', symbol: 'BTC' },
  ethereum: { id: 'ethereum', symbol: 'ETH' },
  eth: { id: 'ethereum', symbol: 'ETH' },
  solana: { id: 'solana', symbol: 'SOL' },
  sol: { id: 'solana', symbol: 'SOL' },
};

function getCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function setCache(cache) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    console.warn('Failed to save crypto cache', e);
  }
}

export function detectCryptoAsset(url, title = '') {
  if (!url) return null;
  const target = (url + ' ' + title).toLowerCase();

  for (const [key, val] of Object.entries(COIN_MAP)) {
    // Check for exact keywords or URL slug patterns
    const regex = new RegExp(`\\b${key}\\b|coins\\/${val.id}|symbols\\/${val.symbol}`, 'i');
    if (regex.test(target)) {
      return val;
    }
  }
  return null;
}

export function formatUsd(num) {
  if (!num && num !== 0) return '$0';
  if (num >= 1000) {
    return '$' + Math.round(num).toLocaleString('en-US');
  }
  return '$' + Number(num).toFixed(2);
}

export async function fetchCryptoTelemetry(url, title = '') {
  const asset = detectCryptoAsset(url, title);
  if (!asset) return null;

  const cache = getCache();
  const cached = cache[asset.id];
  const now = Date.now();

  if (cached && (now - cached.timestamp < CACHE_TTL_MS)) {
    return cached.data;
  }

  try {
    const res = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${asset.id}&vs_currencies=usd&include_24hr_change=true`);
    if (res.status === 200) {
      const data = await res.json();
      const coinData = data[asset.id];
      if (coinData) {
        const change = coinData.usd_24h_change || 0;
        const telemetry = {
          symbol: asset.symbol,
          price: formatUsd(coinData.usd),
          change: (change >= 0 ? '+' : '') + change.toFixed(1) + '%',
          isPositive: change >= 0,
        };

        cache[asset.id] = { timestamp: now, data: telemetry };
        setCache(cache);
        return telemetry;
      }
    }
  } catch (e) {
    if (cached) return cached.data;
  }

  return cached ? cached.data : null;
}

export function getCachedCryptoSync(url, title = '') {
  const asset = detectCryptoAsset(url, title);
  if (!asset) return null;
  const cache = getCache();
  const cached = cache[asset.id];
  return cached ? cached.data : null;
}
